import { Container, Graphics, Sprite, Texture, type Text } from "pixi.js";
import type { GameVisualAssets } from "../../assets/GameVisualAssets";
import { CardFlipView } from "./CardFlipView";
import { Button, fitScale, resolveResponsive } from "@slot/presentation";
import type { FitLimits, PresentationTaskRunner, ViewportStore } from "@slot/presentation";

import { createLocalizedModalLabel, createModalLabel } from "@slot/game-shell";
import type { LocalizationService } from "@slot/game-shell";

const DIM_ALPHA = 219 / 255;
const OUTLINE = 1;
const PALE_GOLD = 0xfaf28d;
const YELLOW = 0xeeee5f;
const WHITE = 0xffffff;
const BROWN = 0x63452e;
const ATTEMPTS = 0xe9e1a6;
/** `cc.Button` SCALE transition, default `zoomScale`. */
const BUTTON_ZOOM = 1.2;

/** `ModalLayer/GameModalContainer/GambleView/root` geometry from the scene dump (authored y-up, stored y-down). */
const GAMBLE_GEOMETRY = {
  content: { width: 900, height: 1000, limits: { desktop: { maxWidth: 900 }, mobile: { maxWidth: 900 }, mobile_landscape: { maxWidth: 1200 } } },
  background: { width: 900, height: 1089 },
  top: { y: -339.2, yourWin: { y: -125.3, fontSize: 46.3 }, currentWin: { y: -41.5, fontSize: 80.6 }, gambleToWin: { y: 65.0, fontSize: 53 }, potentialWin: { y: 150.7, fontSize: 100.5 } },
  options: { y: 69.1, x: 165.8, buttonY: 17.9, width: 221, height: 291, redLabelY: -13.8, blackLabelY: -15.4, fontSize: 37.3 },
  result: {
    y: 69.1,
    card: { x: -1.2, y: 63.4, back: { x: 2.4, y: -9.7, width: 192, height: 244, labelX: -2.4, labelY: -4.1 }, red: { width: 200, height: 264 }, black: { width: 203, height: 265 }, labelY: -13.8, fontSize: 37.3 },
    title: { y: -119.9, width: 418, height: 134, labelX: 4.5, labelY: 6.1, fontSize: 40.9 },
    flipHalfDurationSeconds: 0.15,
    preFlipHoldSeconds: 0.45,
  },
  collect: { y: 350.9, width: 423, height: 135, labelY: 16.2, fontSize: 37.3 },
  attempts: { y: 442.4, fontSize: 25 },
} as const;

/**
 * The gamble UI — a thin view layer, no logic. The presenter (`CardFlipGamblePresenter`) drives
 * visibility and outcome through the public methods. Tree (scene dump): root > background (dim +
 * tap swallow), Content (900×1000 scale-mode window layout) > background sprite, Top labels,
 * OptionsContainer (Red / Black), ResultContainer (Card + Win / Lost titles), CollectButton,
 * AttemptsLabel. Everything is UI chrome (the flip rides the chrome runner).
 */
export class GambleView extends Container {
  /** Parent of the gamble option buttons — children in server option order (Red, Black). */
  public readonly optionsContainer: Container;
  public readonly collectButton: Button;
  public readonly cardFlipView: CardFlipView;
  private readonly root: Container;
  private readonly dim: Graphics;
  private readonly currentWinLabel: Text;
  private readonly potentialWinLabel: Text;
  private readonly attemptsLabel: Text;
  private readonly resultRevealArea: Container;
  private readonly winTitleNode: Container;
  private readonly loseTitleNode: Container;
  private unsubscribeViewport: (() => void) | null = null;

  public constructor(input: {
    assets: GameVisualAssets;
    viewport: ViewportStore;
    localization: LocalizationService;
    runner: PresentationTaskRunner;
  }) {
    super({ label: "GamblePanel" });
    const g = GAMBLE_GEOMETRY;
    const { localization } = input;
    const sheet = input.assets.sheets.gamble;
    const frame = (name: string): Texture => sheet?.textures[name] ?? Texture.EMPTY;
    const sprite = (name: string, width: number, height: number, label: string): Sprite => {
      const s = new Sprite({ texture: frame(name), label });
      s.anchor.set(0.5);
      s.setSize(width, height);
      return s;
    };

    this.root = new Container({ label: "root" });
    this.root.visible = false;
    this.addChild(this.root);
    this.dim = new Graphics();
    this.dim.label = "background";
    this.dim.eventMode = "static";
    this.root.addChild(this.dim);

    const content = new Container({ label: "Content" });
    this.root.addChild(content);

    content.addChild(sprite("background", g.background.width, g.background.height, "background"));

    const top = new Container({ label: "Top" });
    top.position.set(0, g.top.y);
    this.currentWinLabel = createModalLabel({ ...g.top.currentWin, color: YELLOW, weight: "700", outline: OUTLINE, label: "CurrentWinAmount" });
    this.potentialWinLabel = createModalLabel({ ...g.top.potentialWin, color: YELLOW, weight: "700", outline: OUTLINE, label: "PotentialWinAmount" });
    top.addChild(
      createLocalizedModalLabel({ localization, key: "gamble.label.your_win", fallback: "Your Win", ...g.top.yourWin, color: PALE_GOLD, weight: "700", outline: OUTLINE, label: "YourWinLabel" }),
      this.currentWinLabel,
      createLocalizedModalLabel({ localization, key: "gamble.label.gamble_to_win", fallback: "Gamble To Win", ...g.top.gambleToWin, color: PALE_GOLD, weight: "700", outline: OUTLINE, label: "GambleToWinLabel" }),
      this.potentialWinLabel,
    );
    content.addChild(top);

    const o = g.options;
    this.optionsContainer = new Container({ label: "OptionsContainer" });
    this.optionsContainer.position.set(0, o.y);
    const option = (name: string, key: string, fallback: string, x: number, labelY: number, label: string): Button => {
      const view = new Container({ label: "view" });
      view.addChild(sprite(name, o.width, o.height, "sprite"), createLocalizedModalLabel({ localization, key, fallback, y: labelY, fontSize: o.fontSize, color: WHITE, weight: "700", outline: OUTLINE, label: "Label" }));
      const button = new Button({ view, pressedScale: BUTTON_ZOOM, label });
      button.position.set(x, o.buttonY);
      return button;
    };
    this.optionsContainer.addChild(
      option("redCard", "gamble.option.red", "Red", -o.x, o.redLabelY, "RedButton"),
      option("blackCard", "gamble.option.black", "Black", o.x, o.blackLabelY, "BlackButton"),
    );
    content.addChild(this.optionsContainer);

    const r = g.result;
    this.resultRevealArea = new Container({ label: "ResultContainer" });
    this.resultRevealArea.position.set(0, r.y);
    const face = (name: string, width: number, height: number, label: string, text: { key?: string; fallback: string; x?: number; y: number }): Container => {
      const node = new Container({ label });
      const textLabel = text.key
        ? createLocalizedModalLabel({ localization, key: text.key, fallback: text.fallback, y: text.y, fontSize: r.card.fontSize, color: WHITE, weight: "700", outline: OUTLINE, label: "Label" })
        : createModalLabel({ y: text.y, fontSize: r.card.fontSize, color: WHITE, weight: "700", outline: OUTLINE, label: "Label" });
      if (!text.key) textLabel.text = text.fallback;
      textLabel.position.x = text.x ?? 0;
      node.addChild(sprite(name, width, height, "sprite"), textLabel);
      return node;
    };
    const backFace = face("cardBackface", r.card.back.width, r.card.back.height, "BackFace", { fallback: "?", x: r.card.back.labelX, y: r.card.back.labelY });
    backFace.position.set(r.card.back.x, r.card.back.y);
    this.cardFlipView = new CardFlipView({
      backFace,
      redFace: face("redCard", r.card.red.width, r.card.red.height, "RedFace", { key: "gamble.option.red", fallback: "Red", y: r.card.labelY }),
      blackFace: face("blackCard", r.card.black.width, r.card.black.height, "BlackFace", { key: "gamble.option.black", fallback: "Black", y: r.card.labelY }),
      runner: input.runner,
      flipHalfDurationSeconds: r.flipHalfDurationSeconds,
      preFlipHoldSeconds: r.preFlipHoldSeconds,
    });
    this.cardFlipView.position.set(r.card.x, r.card.y);
    const title = (name: string, key: string, fallback: string, label: string): Container => {
      const node = new Container({ label });
      node.position.set(0, r.title.y);
      const text = createLocalizedModalLabel({ localization, key, fallback, y: r.title.labelY, fontSize: r.title.fontSize, color: WHITE, weight: "700", outline: OUTLINE, label: "ResultLabel" });
      text.position.x = r.title.labelX;
      node.addChild(sprite(name, r.title.width, r.title.height, "Sprite"), text);
      return node;
    };
    this.winTitleNode = title("winTitle", "gamble.result.won", "You Won", "WinTitle");
    this.loseTitleNode = title("lostTitle", "gamble.result.loose", "You Lost", "LostTitle");
    this.resultRevealArea.addChild(this.cardFlipView, this.winTitleNode, this.loseTitleNode);
    content.addChild(this.resultRevealArea);

    const c = g.collect;
    const collectView = new Container({ label: "view" });
    collectView.addChild(
      sprite("collectButton", c.width, c.height, "sprite"),
      createLocalizedModalLabel({ localization, key: "gamble.action.collect", fallback: "Collect", y: c.labelY, fontSize: c.fontSize, color: BROWN, weight: "700", outline: OUTLINE, label: "Label" }),
    );
    this.collectButton = new Button({ view: collectView, pressedScale: BUTTON_ZOOM, label: "CollectButton" });
    this.collectButton.position.set(0, c.y);
    content.addChild(this.collectButton);

    this.attemptsLabel = createModalLabel({ ...g.attempts, color: ATTEMPTS, weight: "300", outline: OUTLINE, label: "AttemptsLabel" });
    content.addChild(this.attemptsLabel);

    this.unsubscribeViewport = input.viewport.subscribe((v) => {
      const { width, height } = v.design;
      this.dim.clear().rect(-width / 2, -height / 2, width, height).fill({ color: 0x000000, alpha: DIM_ALPHA });
      const scale = fitScale(g.content, resolveResponsive<FitLimits>(g.content.limits, v.shape), v.design);
      content.scale.set(scale, scale);
    });
    this.setResultOutcome("none");
  }

  public show(): void {
    this.root.visible = true;
  }

  public hide(): void {
    this.root.visible = false;
  }

  public setCurrentWin(text: string): void {
    this.currentWinLabel.text = text;
  }

  public setPotentialWin(text: string): void {
    this.potentialWinLabel.text = text;
  }

  public setAttemptsText(text: string): void {
    this.attemptsLabel.text = text;
  }

  public setCollectEnabled(enabled: boolean): void {
    this.collectButton.visible = enabled;
  }

  public setOptionsEnabled(enabled: boolean): void {
    this.optionsContainer.visible = enabled;
  }

  public setResultVisible(visible: boolean): void {
    this.resultRevealArea.visible = visible;
  }

  /** Toggles the pre-composed title nodes based on gamble outcome. "none" hides both. */
  public setResultOutcome(outcome: "win" | "lose" | "none"): void {
    this.winTitleNode.visible = outcome === "win";
    this.loseTitleNode.visible = outcome === "lose";
  }

  public dispose(): void {
    this.unsubscribeViewport?.();
    this.unsubscribeViewport = null;
    this.destroy({ children: true });
  }
}
